'use client';
import React from 'react';
import { Star, Quote } from 'lucide-react';

interface SocialProofProps {
  language: 'en' | 'ar';
}

const clientLogos = [
  { nameEn: 'Basra Oil Company', nameAr: 'شركة نفط البصرة', abbr: 'BOC' },
  { nameEn: 'Iraqi Manufacturing Group', nameAr: 'مجموعة التصنيع العراقي', abbr: 'IMG' },
  { nameEn: 'Al-Furat EPC Contractors', nameAr: 'مقاولو الفرات EPC', abbr: 'FEPC' },
  { nameEn: 'Baghdad Power Authority', nameAr: 'هيئة كهرباء بغداد', abbr: 'BPA' },
  { nameEn: 'Kirkuk Industrial Zone', nameAr: 'منطقة كركوك الصناعية', abbr: 'KIZ' },
];

const testimonials = [
  {
    quoteEn: 'Swift replies, genuine parts, and they handled all customs. Exactly the partner we needed.',
    quoteAr: 'ردود سريعة، قطع أصلية، وتولّوا كل إجراءات الجمارك. بالضبط الشريك الذي كنا نحتاجه.',
    authorEn: 'Plant Manager, Basra',
    authorAr: 'مدير مصنع، البصرة',
    company: 'Basra Oil Company',
    rating: 5,
  },
  {
    quoteEn: 'PLC-IRAQ saved our production line. They had the Siemens CPU in stock when no one else did.',
    quoteAr: 'PLC-IRAQ أنقذت خط إنتاجنا. كان لديهم وحدة CPU سيمنز في المخزن حين لم يكن لدى أحد غيرهم.',
    authorEn: 'Automation Engineer, Baghdad',
    authorAr: 'مهندس أتمتة، بغداد',
    company: 'Iraqi Manufacturing Group',
    rating: 5,
  },
  {
    quoteEn: 'Professional team, fast delivery, and the on-site commissioning service was outstanding.',
    quoteAr: 'فريق محترف، تسليم سريع، وخدمة التشغيل الميداني كانت ممتازة.',
    authorEn: 'Project Manager, Kirkuk',
    authorAr: 'مدير مشروع، كركوك',
    company: 'Al-Furat EPC Contractors',
    rating: 5,
  },
];

export default function SocialProof({ language }: SocialProofProps) {
  return (
    <section id="about" className="py-16 bg-white" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-onyx font-space-grotesk mb-3">
            {language === 'ar' ? 'موثوق به من قبل الرواد الصناعيين في العراق' : "Trusted by Iraq's Industrial Leaders"}
          </h2>
          <p className="text-slate-grey font-roboto">
            {language === 'ar' ?'شركات من قطاعات النفط والغاز والتصنيع والبنية التحتية تثق بنا' :'Companies across oil & gas, manufacturing, and infrastructure trust PLC-IRAQ'}
          </p>
        </div>

        {/* Client Logos */}
        <div className="flex flex-wrap justify-center items-center gap-6 mb-14">
          {clientLogos.map((logo, idx) => (
            <div
              key={idx}
              className="flex items-center justify-center w-40 h-16 bg-light-grey border border-slate-200 rounded-lg grayscale hover:grayscale-0 transition-all duration-300 hover:border-iraqi-red/30 hover:shadow-card"
              aria-label={language === 'ar' ? logo.nameAr : logo.nameEn}
            >
              <span className="text-sm font-bold text-slate-400 font-space-grotesk tracking-wide">{logo.abbr}</span>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, idx) => (
            <div
              key={idx}
              className="bg-light-grey border border-slate-200 rounded-xl p-6 transition-all duration-300 hover:shadow-card-hover hover:border-iraqi-red/20"
            >
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {Array.from({ length: testimonial.rating }).map((_, sIdx) => (
                  <Star key={sIdx} size={16} className="text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              {/* Quote Icon */}
              <Quote size={20} className="text-iraqi-red/30 mb-3" />
              {/* Quote Text */}
              <p className="text-sm text-slate-700 font-roboto leading-relaxed mb-4 italic">
                &ldquo;{language === 'ar' ? testimonial.quoteAr : testimonial.quoteEn}&rdquo;
              </p>
              {/* Attribution */}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-iraqi-red/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-iraqi-red font-bold text-sm font-space-grotesk">
                    {testimonial.company.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="text-xs font-semibold text-onyx font-space-grotesk">
                    {language === 'ar' ? testimonial.authorAr : testimonial.authorEn}
                  </div>
                  <div className="text-xs text-slate-grey font-roboto">{testimonial.company}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Row */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: '500+', labelEn: 'Iraqi Clients Served', labelAr: 'عميل عراقي' },
            { value: '99.2%', labelEn: 'Parts Authenticity Rate', labelAr: 'معدل أصالة القطع' },
            { value: '48hr', labelEn: 'Avg. Local Delivery', labelAr: 'متوسط التسليم' },
            { value: '7+', labelEn: 'Years in Iraq', labelAr: 'سنوات في العراق' },
          ].map((stat, idx) => (
            <div key={idx} className="text-center p-6 bg-light-grey rounded-xl border border-slate-200">
              <div className="text-3xl font-bold text-iraqi-red font-space-grotesk mb-1">{stat.value}</div>
              <div className="text-sm text-slate-grey font-roboto">{language === 'ar' ? stat.labelAr : stat.labelEn}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
